import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FactCheck } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { AdminAuditEvent, AuditResult } from '@/api/admin';
import { toApiError } from '@/api/client';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/auditoria/')({
  component: AdminAuditPage,
});

const RESULT_META: Record<AuditResult, { label: string; color: 'success' | 'error' }> = {
  Success: { label: 'Éxito', color: 'success' },
  Failure: { label: 'Falla', color: 'error' },
};

// Acciones de mayor señal para el filtro (el backend acepta cualquier nombre de AuditAction).
const ACTIONS = [
  'UserLogin',
  'UserLoginFailed',
  'ImpersonationStart',
  'TenantSuspend',
  'TenantReactivate',
  'PlanUpgrade',
  'TenantBillingExtend',
  'UserDisable',
  'UserReenable',
  'UserInvite',
  'CertificateUpload',
  'CredentialIssue',
];

function metadataPreview(m: unknown): string {
  if (m == null) return '';
  try {
    const s = typeof m === 'string' ? m : JSON.stringify(m);
    return s === '{}' || s === '""' ? '' : s.length > 80 ? `${s.slice(0, 80)}…` : s;
  } catch {
    return '';
  }
}

function AdminAuditPage(): JSX.Element {
  const [result, setResult] = useState<AuditResult | ''>('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const skip = page * rowsPerPage;
  const q = useQuery({
    queryKey: ['admin-audit', { result, action, skip, take: rowsPerPage }],
    queryFn: () =>
      adminApi.listAuditEvents({
        result: result || undefined,
        action: action || undefined,
        skip,
        take: rowsPerPage,
      }),
    placeholderData: keepPreviousData,
  });

  const rows: AdminAuditEvent[] = q.data?.items ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactCheck color="primary" /> Auditoría
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registro cross-tenant de acciones, logins e impersonaciones. Los eventos son inmutables.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Resultado
        </Typography>
        <Select
          size="small"
          value={result}
          displayEmpty
          onChange={(e) => {
            setResult(e.target.value as AuditResult | '');
            setPage(0);
          }}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="Success">Éxito</MenuItem>
          <MenuItem value="Failure">Falla</MenuItem>
        </Select>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Acción
        </Typography>
        <Select
          size="small"
          value={action}
          displayEmpty
          onChange={(e) => {
            setAction(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {q.isError ? (
        <Alert severity="error">{toApiError(q.error).message}</Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" aria-label="Audit log">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha/hora</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell>Recurso</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {q.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Sin eventos para el filtro actual.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((e) => {
                    const detail = e.failureReason ?? metadataPreview(e.metadata);
                    return (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.timestamp)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{e.actorEmail ?? '—'}</Typography>
                          {e.actorIpAddress ? (
                            <Typography variant="caption" color="text.secondary">
                              {e.actorIpAddress}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{e.action}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={RESULT_META[e.result].color}
                            variant={e.result === 'Failure' ? 'filled' : 'outlined'}
                            label={RESULT_META[e.result].label}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {e.resourceType}
                            {e.resourceId ? ` · ${e.resourceId.slice(0, 8)}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          {detail ? (
                            <Tooltip title={detail}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: 'var(--font-mono)',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {detail}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={q.data?.total ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página:"
          />
        </Paper>
      )}
    </Box>
  );
}
